import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { getData, postDataWithBody } from "../../../Services/Services";
import {
    getalltriplist,
    getvehicles,
    cancelTrip,
    getAllResons,
    getAllVehicles,
} from "../../../Services/EndPoints";
import LoadingOverlay from "../ExpectedTrackForm/LoadingOverlay";
import TableCustomRowsCols from "../../../Components/UiComponents/Table/TableCustomRowsCols";
import { ChevronDown, X, RouteOff, Map, Eye, Route } from "lucide-react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { EditConvgIcon } from "../../../Components/IconComponents";
import "../PlannedRoute/PlannedRoute.css";
import useModulePermissions, { FLEET_PERMISSION_IDS } from "../Common/hooks/useModulePermissions";
import { usePlannedRouteState } from "./PlannedRouteStateContext";

const PlannedRoute = () => {
    // Role's Edit right for Planned Route (web_permissions from getUserProfile).
    // Edit covers both "Edit Route" and "Cancel Route" — Delete isn't assignable for this module.
    const { canEdit } = useModulePermissions(FLEET_PERMISSION_IDS.PLANNED_ROUTE);
    const [isLoading, setIsLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const [showCancelPopup, setShowCancelPopup] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [cancelComment, setCancelComment] = useState("");
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancellationReasons, setCancellationReasons] = useState([]);
    const [selectedReasonId, setSelectedReasonId] = useState("");
    const [customReason, setCustomReason] = useState("");

    // global state for form + list
    const { plannedSearchState, setPlannedSearchState } = usePlannedRouteState();
    const { formData, searchTerm, vehicleTrackingData, rowCount } =
        plannedSearchState;

    useEffect(() => {
        fetchVehicles();
        fetchCancellationReasons();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchVehicles = async () => {
        try {
            setIsLoading(true);
            // const response = await getData(getvehicles);
            const response = await getData(getAllVehicles);
            if (Array.isArray(response)) {
                setVehicles(response.map((v) => ({
                    id: v.id,
                    vehicleNumber:      v.vehicle_number,
                    vehicleName:        v.vehicle_number,
                    vehicleType:        v.vehicle_type        || '',
                    brand:              v.brand               || '',
                    model:              v.model               || '',
                    fuelType:           v.fuel_type           || '',
                    registrationNumber: v.registration_number || '',
                    driverName:         v.driver_name         || '',
                    driverPhone:        v.driver_phone        || '',
                    driverLicense:      v.driver_license      || '',
                    licenseExpiry:      v.license_expiry      || '',
                })));
            }
        } catch (error) {
            console.error("Error fetching vehicles:", error);
            toast.error("Failed to fetch vehicles");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCancellationReasons = async () => {
        try {
            setIsLoading(true);
            const response = await getData(getAllResons);
            if (response) {
                setCancellationReasons(response);
            }
        } catch (error) {
            console.error("Error fetching cancellation reasons:", error);
            toast.error("Failed to fetch cancellation reasons");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "fromDate") {
            setPlannedSearchState((prev) => {
                const newFormData = {
                    ...prev.formData,
                    [name]: value,
                };

                if (
                    prev.formData.toDate &&
                    value &&
                    new Date(value) > new Date(prev.formData.toDate)
                ) {
                    newFormData.toDate = "";
                    toast.warning(
                        "To Date has been cleared as it was before the new From Date"
                    );
                }

                return {
                    ...prev,
                    formData: newFormData,
                };
            });
        } else {
            setPlannedSearchState((prev) => ({
                ...prev,
                formData: {
                    ...prev.formData,
                    [name]: value,
                },
            }));
        }
    };

    const handleVehicleToggle = (vehicleId) => {
        setPlannedSearchState((prev) => {
            const isSelected = prev.formData.vehicleIds.includes(vehicleId);
            return {
                ...prev,
                formData: {
                    ...prev.formData,
                    vehicleIds: isSelected
                        ? prev.formData.vehicleIds.filter((id) => id !== vehicleId)
                        : [...prev.formData.vehicleIds, vehicleId],
                },
            };
        });
    };

    const filteredVehicles = vehicles.filter((vehicle) =>
        vehicle.vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelectAll = () => {
        const allVehicleIds = filteredVehicles.map((v) => v.id);
        const allSelected = allVehicleIds.every((id) =>
            formData.vehicleIds.includes(id)
        );

        setPlannedSearchState((prev) => ({
            ...prev,
            formData: {
                ...prev.formData,
                vehicleIds: allSelected
                    ? []
                    : [...new Set([...prev.formData.vehicleIds, ...allVehicleIds])],
            },
        }));
    };

    const removeVehicle = (vehicleId, e) => {
        e.stopPropagation();
        setPlannedSearchState((prev) => ({
            ...prev,
            formData: {
                ...prev.formData,
                vehicleIds: prev.formData.vehicleIds.filter((id) => id !== vehicleId),
            },
        }));
    };

    const handleCancelClick = (route) => {
        setSelectedRoute(route);
        setSelectedReasonId("");
        setCancelComment("");
        setCustomReason("");
        setShowCancelPopup(true);
    };

    const handleReasonChange = (e) => {
        const value = e.target.value;
        setSelectedReasonId(value);
        if (value !== "other") {
            setCustomReason("");
        }
    };

    const splitDateTime = (dateString) => {
        if (!dateString) return { date: "", time: "" };

        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        return {
            date: `${day}-${month}-${year}`,
            time: `${hours}:${minutes} ${ampm}`,
        };
    };

    const handleCancelSubmit = async () => {
        if (!selectedReasonId) {
            toast.error("Please select a cancellation reason", {
                position: "top-center",
            });
            return;
        }
        setIsCancelling(true);
        try {
            let reasonText = "";

            if (selectedReasonId === "other") {
                reasonText = customReason.trim() || "Other reason";
            } else {
                const selectedReason = cancellationReasons.find(
                    (reason) => reason.id.toString() === selectedReasonId
                );
                reasonText = selectedReason ? selectedReason.reason : "Unknown reason";
            }

            const requestBody = {
                id: selectedRoute.id,
                cancelledBy: selectedRoute.createdBy,
                status: selectedRoute.status,
                comments: cancelComment.trim(),
                reason: reasonText,
            };

            const response = await postDataWithBody(cancelTrip, requestBody, {});

            if (response && response.status === "success") {
                toast.success("Route cancelled successfully", {
                    position: "top-center",
                });
                await handleSubmit(true);
            } else {
                toast.error(response?.message || "Failed to cancel route", {
                    position: "top-center",
                });
            }

            setShowCancelPopup(false);
            setSelectedRoute(null);
            setCancelComment("");
            setSelectedReasonId("");
            setCustomReason("");
        } catch (error) {
            console.error("Error cancelling route:", error);
            toast.error("Failed to cancel route. Please try again.", {
                position: "top-center",
            });
        } finally {
            setIsCancelling(false);
        }
    };

    const handleCancelClose = () => {
        setShowCancelPopup(false);
        setSelectedRoute(null);
        setSelectedReasonId("");
        setCancelComment("");
        setCustomReason("");
    };

    const validateForm = () => {
        if (formData.vehicleIds.length === 0) {
            toast.error("Please select at least one vehicle", {
                position: "top-center",
            });
            return false;
        }
        if (!formData.fromDate) {
            toast.error("Please select from date", {
                position: "top-center",
            });
            return false;
        }
        if (!formData.toDate) {
            toast.error("Please select to date", {
                position: "top-center",
            });
            return false;
        }

        const fromDate = new Date(formData.fromDate);
        const toDate = new Date(formData.toDate);
        if (fromDate > toDate) {
            toast.error("To date must be equal to or after from date", {
                position: "top-center",
            });
            return false;
        }

        return true;
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Vehicle Number",
                accessor: "vehicleNumber",
            },
            {
                Header: "Route Name",
                accessor: "routeName",
                disableSortBy: true,
            },
            {
                Header: () => <>Planned<br />Start Date</>,
                accessor: "startDate",
                width: 70,
                Cell: ({ value }) => {
                    const { date, time } = splitDateTime(value);
                    return (
                        <div style={{ fontSize: 12 }}>
                            <div>{date}</div>
                            <div style={{ color: "#64748b" }}>{time}</div>
                        </div>
                    );
                },
            },
            {
                Header: () => <>Planned<br />End Date</>,
                accessor: "endDate",
                disableSortBy: true,
                width: 70,
                Cell: ({ value }) => {
                    const { date, time } = splitDateTime(value);
                    return (
                        <div style={{ fontSize: 12 }}>
                            <div>{date}</div>
                            <div style={{ color: "#64748b" }}>{time}</div>
                        </div>
                    );
                },
            },
            {
                Header: () => <>Travelled<br />Start Date</>,
                accessor: "actualStartDate",
                width: 70,
                Cell: ({ value }) => {
                    if (!value) return <span style={{ color: "#94a3b8" }}>N/A</span>;
                    const { date, time } = splitDateTime(value);
                    return (
                        <div style={{ fontSize: 12 }}>
                            <div>{date}</div>
                            <div style={{ color: "#64748b" }}>{time}</div>
                        </div>
                    );
                },
            },
            {
                Header: () => <>Travelled<br />End Date</>,
                accessor: "actualEndDate",
                disableSortBy: true,
                width: 70,
                Cell: ({ value }) => {
                    if (!value) return <span style={{ color: "#94a3b8" }}>N/A</span>;
                    const { date, time } = splitDateTime(value);
                    return (
                        <div style={{ fontSize: 12 }}>
                            <div>{date}</div>
                            <div style={{ color: "#64748b" }}>{time}</div>
                        </div>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status",
                disableSortBy: true,
                Cell: ({ value }) => {
                    const formatStatus = (status) => {
                        switch (status) {
                            case "TRIP_TERMINATED_INCIDENT":
                                return "TRIP TERMINATED - INCIDENT";
                            case "GPS_UNAVAILABLE":
                                return "GPS UNAVAILABLE";
                            case "IN PROGRESS":
                                return "IN PROGRESS";
                            case "TRAVELED":
                                return "TRAVELED";
                            case "NOT TRAVELED":
                                return "NOT TRAVELED";
                            case "CANCELLED":
                                return "CANCELLED";
                            default:
                                return status || "Unknown";
                        }
                    };

                    const displayStatus = formatStatus(value);
                    let textColor = "";

                    switch (value) {
                        case "IN PROGRESS":
                            textColor = "#f59e0b";
                            break;
                        case "TRAVELED":
                            textColor = "#16a34a";
                            break;
                        case "NOT TRAVELED":
                            textColor = "red";
                            break;
                        case "CANCELLED":
                            textColor = "red";
                            break;
                        case "TRIP_TERMINATED_INCIDENT":
                            textColor = "#dc2626";
                            break;
                        case "GPS_UNAVAILABLE":
                            textColor = "#f59e0b";
                            break;
                        default:
                            textColor = "#1a7a73";
                            break;
                    }

                    let bgColor = "";
                    switch (value) {
                        case "IN PROGRESS":    bgColor = "#fef3cd"; break;
                        case "TRAVELED":       bgColor = "#dcfce7"; break;
                        case "NOT TRAVELED":   bgColor = "#fee2e2"; break;
                        case "CANCELLED":      bgColor = "#fee2e2"; break;
                        case "TRIP_TERMINATED_INCIDENT": bgColor = "#fee2e2"; break;
                        case "GPS_UNAVAILABLE": bgColor = "#fef3cd"; break;
                        default:               bgColor = "#ccfbf1"; break;
                    }

                    return (
                        <span
                            style={{
                                color: textColor,
                                backgroundColor: bgColor,
                                padding: "3px 10px",
                                borderRadius: "20px",
                                fontSize: "11px",
                                fontWeight: "700",
                                display: "inline-block",
                                letterSpacing: "0.3px",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {displayStatus || "Unknown"}
                        </span>
                    );
                },
            },
            {
                Header: t("Travel View"),
                disableSortBy: true,
                Cell: ({ row }) => {
                    const route = row.original;
                    const isTraveled = route.status === "TRAVELED";
                    const isRolloverEnded = route.status === "TRIP_TERMINATED_INCIDENT";

                    return (
                        <div
                            className="usertable__actionsicon"
                            style={{ marginLeft: "10pt" }}
                        >
                            {isTraveled || isRolloverEnded ? (
                                <span
                                    onClick={() => {
                                        const rowNum = (row.original.vehicleNumber || '').trim().toLowerCase();
                                        const match  = vehicles.find(v => (v.vehicleNumber || '').trim().toLowerCase() === rowNum);
                                        navigate("/RoutePlanning/travel-view", {
                                            state: {
                                                data: row.original,
                                                edit: true,
                                                vehicleType: match?.vehicleType || '',
                                                vehicleData: match || null,
                                            },
                                        });
                                    }}
                                    style={{ cursor: "pointer" }}
                                    title="Travel View"
                                >
                                    <Map
                                        className="cursor-pointer me-2"
                                        size={16}
                                        color="green"
                                    />
                                </span>
                            ) : (
                                <span
                                    style={{
                                        cursor: "not-allowed",
                                        opacity: 0.4,
                                    }}
                                    title="Travel view available only for traveled routes"
                                >
                                    <Map className="me-2" size={16} color="green" />
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: t("Live Tracking"),
                disableSortBy: true,
                className: "icon-center",
                Cell: ({ row }) => {
                    const route = row.original;
                    const isInProgress = route.status === "IN PROGRESS";
                    const isTraveled = route.status === "TRAVELED";

                    return (
                        <div
                            className="usertable__actionsicon"
                            style={{ marginLeft: "18pt" }}
                        >
                            {isInProgress ? (
                                <span
                                    onClick={() => {
                                        const rowNum = (row.original.vehicleNumber || '').trim().toLowerCase();
                                        const match  = vehicles.find(v => (v.vehicleNumber || '').trim().toLowerCase() === rowNum);
                                        navigate("/RoutePlanning/trip-trakingView", {
                                            state: {
                                                data: row.original,
                                                edit: true,
                                                isLiveTracking: true,
                                                vehicleType: match?.vehicleType || '',
                                                vehicleData: match || null,
                                            },
                                        });
                                    }}
                                    style={{ cursor: "pointer" }}
                                    title="Live Tracking"
                                >
                                    <Route
                                        className="cursor-pointer me-2"
                                        size={16}
                                        color="red"
                                    />
                                </span>
                            ) : (
                                <span
                                    style={{
                                        cursor: "not-allowed",
                                        opacity: 0.4,
                                    }}
                                    title={
                                        isTraveled
                                            ? "Live tracking not available for traveled routes"
                                            : "Live tracking available only for in-progress routes"
                                    }
                                >
                                    <Route className="me-2" size={16} color="red" />
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: t("Planned Action"),
                disableSortBy: true,
                className: "icon-center",
                Cell: ({ row }) => {
                    const route = row.original;
                    const routeStartDate = new Date(route.startDate);
                    const today = new Date();

                    const isPastDate =
                        routeStartDate.setHours(0, 0, 0, 0) <
                        today.setHours(0, 0, 0, 0);

                    const isTraveled = route.status === "TRAVELED";
                    const isInProgress = route.status === "IN PROGRESS";
                    const isCancelled = route.status === "CANCELLED";
                    const isRolloverEnded = route.status === "TRIP_TERMINATED_INCIDENT";

                    const isEditDisabled =
                        isPastDate || isTraveled || isInProgress || isCancelled || isRolloverEnded;
                    const isCancelDisabled =
                        isPastDate || isTraveled || isCancelled || isRolloverEnded;

                    return (
                        <div
                            className="usertable__actionsicon"
                            style={{ marginLeft: "8pt" }}
                        >
                            <span
                                onClick={() => {
                                    navigate("/RoutePlanning/planned-view", {
                                        state: {
                                            data: row.original,
                                            edit: true,
                                        },
                                    });
                                }}
                                title="Planned View"
                            >
                                <Eye className="cursor-pointer me-2" size={16} color="blue" />
                            </span>
                            {!canEdit ? null : isEditDisabled ? (
                                <span
                                    className="resource__edit-icon"
                                    style={{
                                        cursor: "not-allowed",
                                        opacity: 0.4,
                                    }}
                                    title={
                                        isTraveled
                                            ? "Cannot edit traveled routes"
                                            : "Cannot edit past routes"
                                    }
                                >
                                    <EditConvgIcon />
                                </span>
                            ) : (
                                <span
                                    className="resource__edit-icon"
                                    onClick={() => {
                                        navigate("/RoutePlanning/exptected-route", {
                                            state: { data: route, edit: true },
                                        });
                                    }}
                                    style={{
                                        cursor: "pointer",
                                        opacity: 1,
                                    }}
                                    title="Edit Route"
                                >
                                    <EditConvgIcon />
                                </span>
                            )}
                            {!canEdit ? null : isCancelDisabled ? (
                                <span
                                    style={{
                                        cursor: "not-allowed",
                                        opacity: 0.4,
                                    }}
                                    title={
                                        isTraveled
                                            ? "Cannot cancel traveled routes"
                                            : isInProgress
                                                ? "Cannot cancel in-progress routes"
                                                : "Cannot cancel past routes"
                                    }
                                >
                                    <RouteOff size={16} color="red" className="ms-2" />
                                </span>
                            ) : (
                                <span
                                    onClick={() => handleCancelClick(route)}
                                    style={{
                                        cursor: "pointer",
                                    }}
                                    title="Cancel Route"
                                >
                                    <RouteOff size={16} color="red" className="ms-2" />
                                </span>
                            )}
                        </div>
                    );
                },
            },
        ],
        [navigate, vehicles, canEdit]
    );

    const handleSubmit = async (suppressToast = false) => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const formatDateTime = (dateString, isEndDate = false) => {
                const date = new Date(dateString);
                const pad = (n) => String(n).padStart(2, "0");

                if (isEndDate) {
                    date.setHours(23, 59, 59);
                } else {
                    date.setHours(0, 0, 0);
                }

                return (
                    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
                        date.getDate()
                    )} ` +
                    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
                        date.getSeconds()
                    )}`
                );
            };

            const selectedVehicleNumbers = formData.vehicleIds
                .map((vehicleId) => {
                    const vehicle = vehicles.find((v) => v.id === vehicleId);
                    return vehicle?.vehicleNumber;
                })
                .filter(Boolean);

            const requestBody = {
                vehicleNumbers: selectedVehicleNumbers,
                startDate: formatDateTime(formData.fromDate, false),
                endDate: formatDateTime(formData.toDate, true),
            };

            const response = await postDataWithBody(getalltriplist, requestBody, {});

            if (response) {
                if (response.status === "success") {
                    if (!suppressToast) {
                        toast.success(
                            response.message || "Planned routes fetched successfully",
                            {
                                position: "top-center",
                                autoClose: 3000,
                            }
                        );
                    }
                    setPlannedSearchState((prev) => ({
                        ...prev,
                        vehicleTrackingData: response.data || [],
                        rowCount: response.data ? response.data.length : 0,
                    }));
                } else {
                    if (!suppressToast) {
                        toast.error(response.message || "Failed to fetch planned routes", {
                            position: "top-center",
                            autoClose: 3000,
                        });
                    }
                    setPlannedSearchState((prev) => ({
                        ...prev,
                        vehicleTrackingData: [],
                        rowCount: 0,
                    }));
                }
            } else {
                toast.info("No planned routes found for selected vehicles", {
                    position: "top-center",
                });
                setPlannedSearchState((prev) => ({
                    ...prev,
                    vehicleTrackingData: [],
                    rowCount: 0,
                }));
            }
        } catch (error) {
            console.error("Error fetching planned routes:", error);
            toast.error("Failed to fetch planned routes. Please try again.", {
                position: "top-center",
            });
            setPlannedSearchState((prev) => ({
                ...prev,
                vehicleTrackingData: [],
                rowCount: 0,
            }));
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setPlannedSearchState((prev) => ({
            ...prev,
            formData: {
                vehicleIds: [],
                fromDate: "",
                toDate: "",
            },
            searchTerm: "",
            vehicleTrackingData: [],
            rowCount: 0,
        }));
        toast.info("Form has been reset", {
            position: "top-center",
        });
    };

    const selectedVehicles = vehicles.filter((v) =>
        formData.vehicleIds.includes(v.id)
    );

    return (
        <div className="row justify-content-center">
            <div className="card" style={{ border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", borderRadius: 10 }}>
                <div className="card-body" style={{ padding: "16px 18px" }}>
                    {isLoading && <LoadingOverlay message="Processing..." />}

                    {showCancelPopup && (
                        <div
                            className="modal show d-block"
                            style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(2px)" }}
                            tabIndex={-1}
                        >
                            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
                                <div className="modal-content" style={{ border: "none", borderRadius: 14, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.28)" }}>
                                    {/* ── Header ── */}
                                    <div style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", padding: "18px 20px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                                        <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/>
                                                <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-8a3.5 3.5 0 0 1 0-7H12"/>
                                                <line x1="2" y1="2" x2="22" y2="22"/>
                                            </svg>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Cancel Route</div>
                                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.82)", marginTop: 2 }}>This action cannot be undone</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCancelClose}
                                            disabled={isCancelling}
                                            style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.35)", cursor: isCancelling ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700, padding: 0, flexShrink: 0 }}
                                        >✕</button>
                                    </div>

                                    {/* ── Route info card ── */}
                                    {selectedRoute && (
                                        <div style={{ margin: "16px 20px 0", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, borderLeft: "4px solid #ef4444", padding: "12px 14px" }}>
                                            <div style={{ fontSize: 9, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>Route to be Cancelled</div>
                                            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                                                <div>
                                                    <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 3 }}>Vehicle</div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{selectedRoute.vehicleNumber || "—"}</div>
                                                </div>
                                                <div style={{ flex: 1, minWidth: 100 }}>
                                                    <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 3 }}>Route Name</div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{selectedRoute.routeName || "—"}</div>
                                                </div>
                                                {selectedRoute.startDate && (
                                                    <div>
                                                        <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 3 }}>Planned Date</div>
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{splitDateTime(selectedRoute.startDate).date}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Warning banner ── */}
                                    <div style={{ margin: "12px 20px 0", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                        </svg>
                                        <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.6 }}>
                                            Cancelling this route will permanently remove it from active schedules. A valid reason is required before proceeding.
                                        </div>
                                    </div>

                                    {/* ── Form body ── */}
                                    <div style={{ padding: "16px 20px 0" }}>
                                        {/* Cancellation Reason */}
                                        <div style={{ marginBottom: 14 }}>
                                            <label htmlFor="cancellationReasons" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                                                Cancellation Reason <span style={{ color: "#ef4444" }}>*</span>
                                            </label>
                                            <select
                                                id="cancellationReasons"
                                                value={selectedReasonId}
                                                onChange={handleReasonChange}
                                                disabled={isCancelling}
                                                style={{ width: "100%", padding: "9px 12px", border: `1px solid ${selectedReasonId ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 8, fontSize: 13, color: selectedReasonId ? "#1e293b" : "#94a3b8", background: "#fff", outline: "none", cursor: isCancelling ? "not-allowed" : "pointer" }}
                                            >
                                                <option value="">Select a cancellation reason</option>
                                                {cancellationReasons.map((reason) => (
                                                    <option key={reason.id} value={reason.id.toString()}>{reason.reason}</option>
                                                ))}
                                                <option value="other">Other</option>
                                            </select>
                                        </div>

                                        {selectedReasonId === "other" && (
                                            <div style={{ marginBottom: 14 }}>
                                                <label htmlFor="customReason" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                                                    Custom Reason
                                                </label>
                                                <input
                                                    type="text"
                                                    id="customReason"
                                                    value={customReason}
                                                    onChange={(e) => setCustomReason(e.target.value)}
                                                    disabled={isCancelling}
                                                    placeholder="Enter your custom reason…"
                                                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", background: "#fff", outline: "none" }}
                                                />
                                            </div>
                                        )}

                                        {/* Comments */}
                                        <div style={{ marginBottom: 4 }}>
                                            <label htmlFor="cancelComment" style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>
                                                Comments
                                            </label>
                                            <textarea
                                                id="cancelComment"
                                                rows={3}
                                                value={cancelComment}
                                                onChange={(e) => setCancelComment(e.target.value)}
                                                placeholder="Provide additional comments for this cancellation…"
                                                disabled={isCancelling}
                                                style={{ width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", background: "#fff", outline: "none", resize: "vertical", lineHeight: 1.5 }}
                                            />
                                        </div>
                                    </div>

                                    {/* ── Footer ── */}
                                    <div style={{ padding: "14px 20px 20px", display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f1f5f9", marginTop: 16 }}>
                                        <button
                                            type="button"
                                            onClick={handleCancelClose}
                                            disabled={isCancelling}
                                            style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: isCancelling ? "not-allowed" : "pointer" }}
                                        >
                                            Discard
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCancelSubmit}
                                            disabled={isCancelling || !selectedReasonId}
                                            style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: isCancelling || !selectedReasonId ? "#fca5a5" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: isCancelling || !selectedReasonId ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8, minWidth: 170, justifyContent: "center" }}
                                        >
                                            {isCancelling && (
                                                <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                                            )}
                                            {isCancelling ? "Cancelling…" : "Submit Cancellation"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filter row */}
                    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "4px solid #0f4a45", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                    <div className="row align-items-end">
                        {/* Vehicle multi-select */}
                        <div className="col-md-3 mb-3 mb-md-0">
                            <label htmlFor="vehicleSelect" className="form-label" style={{ fontSize: 12, fontWeight: 700, color: "#0f4a45", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5 }}>
                                Vehicle Number <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative" ref={dropdownRef}>
                                <div
                                    className="form-control form-control-lg d-flex align-items-center"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    style={{
                                        cursor: "pointer",
                                        minHeight: "33px",
                                        paddingTop: "8px",
                                        paddingBottom: "8px",
                                        paddingLeft: selectedVehicles.length === 0 ? undefined : "8px",
                                        paddingRight: selectedVehicles.length === 0 ? undefined : "8px",
                                        flexWrap: "nowrap",
                                        gap: "4px",
                                    }}
                                >
                                    {selectedVehicles.length === 0 ? (
                                        <span className="text-muted flex-grow-1">
                                            Select Vehicle Numbers
                                        </span>
                                    ) : (
                                        /* Pill chips — same look as the GPS Tracking vehicle multi-select */
                                        <div
                                            className="d-flex align-items-center"
                                            style={{ flex: "1 1 0", minWidth: 0, gap: 4, flexWrap: "wrap" }}
                                            title={selectedVehicles.map(v => v.vehicleNumber).join(", ")}
                                        >
                                            {selectedVehicles.map((vehicle) => (
                                                <span
                                                    key={vehicle.id}
                                                    className="d-inline-flex align-items-center"
                                                    style={{
                                                        gap: 3,
                                                        backgroundColor: "#f1f5f9",
                                                        color: "#334155",
                                                        fontSize: "10px",
                                                        fontWeight: 500,
                                                        lineHeight: 1.6,
                                                        padding: "2px 5px",
                                                        borderRadius: "12px",
                                                        whiteSpace: "nowrap",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {vehicle.vehicleNumber}
                                                    <X
                                                        size={10}
                                                        color="#94a3b8"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => removeVehicle(vehicle.id, e)}
                                                    />
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <ChevronDown
                                        size={16}
                                        color="#94a3b8"
                                        className="ms-auto"
                                        style={{ flexShrink: 0 }}
                                    />
                                </div>

                                {isDropdownOpen && (
                                    <div
                                        className="position-absolute w-100 bg-white border rounded shadow-lg"
                                        style={{
                                            zIndex: 1000,
                                            maxHeight: "300px",
                                            top: "100%",
                                            marginTop: "4px",
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="p-2 border-bottom">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search vehicles..."
                                                value={searchTerm}
                                                onChange={(e) =>
                                                    setPlannedSearchState((prev) => ({
                                                        ...prev,
                                                        searchTerm: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                            <div className="p-2 border-bottom">
                                                <label
                                                    className="d-flex align-items-center mb-0"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            filteredVehicles.length > 0 &&
                                                            filteredVehicles.every((v) =>
                                                                formData.vehicleIds.includes(v.id)
                                                            )
                                                        }
                                                        onChange={handleSelectAll}
                                                        className="me-2"
                                                    />
                                                    <span className="fw-bold">Select All</span>
                                                </label>
                                            </div>
                                            {filteredVehicles.length === 0 ? (
                                                <div className="p-3 text-center text-muted">
                                                    No vehicles found
                                                </div>
                                            ) : (
                                                filteredVehicles.map((vehicle) => (
                                                    <div
                                                        key={vehicle.id}
                                                        className="p-2 hover-bg-light"
                                                    >
                                                        <label
                                                            className="d-flex align-items-center mb-0"
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.vehicleIds.includes(
                                                                    vehicle.id
                                                                )}
                                                                onChange={() => handleVehicleToggle(vehicle.id)}
                                                                className="me-2"
                                                            />
                                                            <span>{vehicle.vehicleNumber}</span>
                                                        </label>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* From Date */}
                        <div className="col-md-2 mb-3 mb-md-0">
                            <label htmlFor="fromDate" className="form-label" style={{ fontSize: 12, fontWeight: 700, color: "#0f4a45", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5 }}>
                                From Date <span className="text-danger">*</span>
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                id="fromDate"
                                name="fromDate"
                                value={formData.fromDate}
                                onChange={handleInputChange}
                                onKeyDown={(e) => e.preventDefault()}
                                style={{ height: 38, fontSize: 13, borderColor: "#d0e4f0" }}
                                required
                            />
                        </div>

                        {/* To Date */}
                        <div className="col-md-2 mb-3 mb-md-0">
                            <label htmlFor="toDate" className="form-label" style={{ fontSize: 12, fontWeight: 700, color: "#0f4a45", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5 }}>
                                To Date <span className="text-danger">*</span>
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                id="toDate"
                                name="toDate"
                                value={formData.toDate}
                                onChange={handleInputChange}
                                onKeyDown={(e) => e.preventDefault()}
                                min={formData.fromDate || undefined}
                                disabled={!formData.fromDate}
                                style={{ height: 38, fontSize: 13, borderColor: "#d0e4f0" }}
                                required
                            />
                        </div>

                        {/* Search button */}
                        <div className="col-md-3 mb-3 mb-md-0">
                            <button
                                type="button"
                                className="btn btn-lg planned-route-btn"
                                onClick={() => handleSubmit(false)}
                                disabled={
                                    isLoading ||
                                    formData.vehicleIds.length === 0 ||
                                    !formData.fromDate ||
                                    !formData.toDate
                                }
                                style={{
                                    width: "100%",
                                    height: 38,
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 6,
                                    letterSpacing: "0.3px",
                                    marginTop: "27px",
                                }}
                            >
                                {isLoading && (
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                )}
                                Search Planned Route
                            </button>
                        </div>

                        {/* Reset button */}
                        <div className="col-md-2 mb-3 mb-md-0">
                            <button
                                type="button"
                                className="btn btn-lg planned-route-btn"
                                onClick={resetForm}
                                disabled={isLoading}
                                style={{
                                    width: "100%",
                                    height: 38,
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    background: "white",
                                    color: "#0f4a45",
                                    border: "1.5px solid #0f4a45",
                                    borderRadius: 6,
                                    letterSpacing: "0.3px",
                                    marginTop: "27px",
                                }}
                            >
                                <i className="fas fa-redo me-2" />
                                Reset Form
                            </button>
                        </div>
                    </div>
                    </div>

                    {/* Table */}
                    <div className="row mt-3" >
                        <div className="col-md-12 planned-route-table" style={{ marginTop: "-15px", padding: "0px" }}>
                            <TableCustomRowsCols
                                data={vehicleTrackingData}
                                title=""
                                showFilter={false}
                                columns={columns}
                                numberOfRows={itemsPerPage}
                                showExport={false}
                                showReset={false}
                                onExpand={false}
                                showSearch={true}
                                isHeaderFixed={true}
                                rowCount={rowCount}
                                headerTableHeadStyle={{
                                    background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)",
                                    color: "#FFFFFF",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1000,
                                    verticalAlign: "top",
                                }}
                                tableStyle={{
                                    border: "1px solid #e8ecf0",
                                    borderRadius: "8px",
                                    marginTop: "8px",
                                    padding: "0px",
                                    overflowY: "auto",
                                    maxHeight: "calc(100vh - 320px)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlannedRoute;
